/**
 * Etapa 3 — Mobiliário e equipamentos: o posto (superfície, alcance, postura, circulação, controles),
 * o quadro de máquinas e equipamentos (gravado na hora) e o cartão de leitura do conforto do setor.
 */
import React, { useCallback, useState } from 'react';
import { Alert, Box, Grid, Typography } from '@mui/material';
import { ergonomicWorkstationService } from '../../../../../services/ergonomicWorkstationService';
import type { ErgonomicEquipment } from '../../../../../types/ergonomicWorkstation';
import { ComfortCard } from '../ComfortCard';
import { ConfirmDialog } from '../dialogs/ItemDialog';
import { EquipmentDialog } from '../dialogs/EquipmentDialog';
import { ItemsBlock } from '../ItemsBlock';
import { useItems } from '../useItems';
import type { WizardStepProps } from '../Wizard.types';
import { errorMessage } from '../wizardValidation';
import { WorkstationField } from '../WorkstationField';

const formatWeight = (weightKg: number | null): string =>
  weightKg === null ? '—' : `${String(Number(weightKg)).replace('.', ',')} kg`;

export const FurnitureStep: React.FC<WizardStepProps> = ({
  workstation,
  values,
  errors,
  canEdit,
  disabled,
  onChange,
  onSaved,
}) => {
  const workstationId = workstation.id;
  const loadEquipments = useCallback(() => ergonomicWorkstationService.getEquipments(workstationId), [workstationId]);
  const equipments = useItems(loadEquipments, 'Não foi possível carregar os equipamentos.');
  const [dialog, setDialog] = useState<{ item: ErgonomicEquipment | null } | null>(null);
  const [removing, setRemoving] = useState<ErgonomicEquipment | null>(null);
  const [removeError, setRemoveError] = useState<string | null>(null);
  const common = { values, errors, onChange, canEdit, disabled };

  const confirmRemoval = async () => {
    if (!removing) return;
    try {
      await ergonomicWorkstationService.deleteEquipment(workstationId, removing.id);
      setRemoving(null);
      await equipments.reload();
      onSaved('Equipamento removido.');
    } catch (err: unknown) {
      setRemoving(null);
      setRemoveError(errorMessage(err, 'Não foi possível remover o equipamento.'));
    }
  };

  const closeDialogSaved = async (message: string) => {
    setDialog(null);
    await equipments.reload();
    onSaved(message);
  };

  return (
    <Box>
      <Typography variant="body1" sx={{ mb: 3 }}>
        Descreva o mobiliário e as máquinas do posto. Tudo aqui é opcional. O conforto do ambiente não se digita: ele é
        lido das medições do setor.
      </Typography>

      <Typography variant="h6" component="h3" sx={{ mb: 2 }}>
        Mobiliário e posto
      </Typography>
      <Grid container spacing={3} sx={{ mb: 5 }}>
        <WorkstationField field="workSurface" label="Superfície de trabalho" multiline help="Bancada, mesa ou piso onde a tarefa é feita." {...common} />
        <WorkstationField field="reachZone" label="Área dentro da zona de alcance" md={6} help="Ex.: 0,60 m, com fácil acesso às peças" {...common} />
        <WorkstationField field="identifiedPosture" label="Posição identificada" md={6} help="Ex.: Em pé por longo período, agachando" {...common} />
        <WorkstationField field="circulationArea" label="Área de circulação" md={6} help="Ex.: Corredor de 1 metro entre a bancada e as caixas" {...common} />
        <WorkstationField field="existingControls" label="Medidas de controle existentes" md={6} help="Ex.: Pausa programada para café" {...common} />
      </Grid>

      {removeError && (
        <Alert severity="error" sx={{ fontSize: '1rem', mb: 2 }} onClose={() => setRemoveError(null)}>
          {removeError}
        </Alert>
      )}
      <ItemsBlock<ErgonomicEquipment>
        title="Máquinas e equipamentos"
        description="Cada máquina ou ferramenta que a pessoa usa neste posto."
        items={equipments.items}
        loading={equipments.loading}
        error={equipments.error}
        onRetry={equipments.reload}
        columns={[
          { id: 'model', label: 'Modelo utilizado', primary: true, render: (row) => row.model },
          { id: 'weight', label: 'Peso', align: 'right', render: (row) => formatWeight(row.weightKg) },
          { id: 'ease', label: 'Facilidade de uso', hideOnMobile: true, render: (row) => row.easeOfUse ?? '—' },
          { id: 'compression', label: 'Compressão do tecido', hideOnMobile: true, render: (row) => row.tissueCompression ?? '—' },
          { id: 'existing', label: 'Controle existente', hideOnMobile: true, render: (row) => row.existingControls ?? '—' },
          { id: 'recommended', label: 'Controle recomendado', hideOnMobile: true, render: (row) => row.recommendedControls ?? '—' },
        ]}
        emptyState={{
          title: 'Nenhum equipamento registrado neste posto',
          description: 'Use "Adicionar equipamento" para cada máquina ou ferramenta usada no posto. Se não há nenhuma, siga em frente.',
        }}
        addLabel="Adicionar equipamento"
        itemName="equipamento"
        itemLabel={(row) => row.model}
        canEdit={canEdit}
        onAdd={() => setDialog({ item: null })}
        onEdit={(row) => setDialog({ item: row })}
        onRemove={setRemoving}
      />

      <ComfortCard workstationId={workstationId} departmentName={workstation.departmentName} />

      {dialog && (
        <EquipmentDialog workstationId={workstationId} item={dialog.item} onSaved={closeDialogSaved} onClose={() => setDialog(null)} />
      )}
      {removing && (
        <ConfirmDialog
          title="Remover este equipamento?"
          message="Ele some do cadastro do posto e do documento."
          onConfirm={confirmRemoval}
          onCancel={() => setRemoving(null)}
        />
      )}
    </Box>
  );
};

export default FurnitureStep;
